import classNames from "classnames";
import React, { useRef } from "react";
import Transition, { TransitionProps } from "react-transition-group/Transition";

import { omit, pick } from "@utils/general";
import { noop } from "@utils/helpers";

import { PolymorphicComponentPropWithRef } from "lib/@types/utils/polymorphic.types";

export type FadeComponentProps = Omit<Partial<TransitionProps>, "children"> & {
    baseClass?: string;
    baseClassActive?: string;
};

export type FadeProps<T extends React.ElementType> =
    PolymorphicComponentPropWithRef<T, FadeComponentProps, "div">;

export const TransitionPropTypeKeys = [
    "in",
    "mountOnEnter",
    "unmountOnExit",
    "appear",
    "enter",
    "exit",
    "timeout",
    "onEnter",
    "onEntering",
    "onEntered",
    "onExit",
    "onExiting",
    "onExited",
];

const Fade = <T extends React.ElementType = "div">({
    ref,
    children,
    className,
    tag,
    baseClass = "fade",
    baseClassActive = "show",
    addEndListener = noop,
    ...otherProps
}: FadeProps<T>) => {
    const Tag = tag || ("div" as React.ElementType);
    const transitionProps = pick(otherProps, TransitionPropTypeKeys);
    const childProps = omit(otherProps, TransitionPropTypeKeys);

    const newRef = useRef(null);

    return (
        <Transition
            in={true}
            appear={true}
            enter={true}
            exit={true}
            timeout={150}
            mountOnEnter={false}
            unmountOnExit={false}
            onEnter={noop}
            onEntering={noop}
            onEntered={noop}
            onExit={noop}
            onExiting={noop}
            onExited={noop}
            addEndListener={addEndListener}
            {...transitionProps}
            nodeRef={ref ? ref : newRef}
        >
            {status => {
                const isActive = status === "entered";
                const classes = classNames(
                    className,
                    baseClass,
                    isActive && baseClassActive,
                );
                return (
                    <Tag
                        className={classes}
                        {...childProps}
                        ref={ref ? ref : newRef}
                    >
                        {children}
                    </Tag>
                );
            }}
        </Transition>
    );
};

export { Fade };
